import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Mail, Lock, User, Eye, EyeOff, Loader, KeyRound } from 'lucide-react';
import { Button } from './ui/button';
import { BACKEND_URL } from '../lib/utils';
import { setAuthToken, authHeaders } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import { toast } from 'sonner';
import { LoginMascot } from './LoginMascot';

// ─── Google Button ─────────────────────────────────────────────────────────────
function GoogleButton({ onClick, loading, t }) {
  return (
    <button onClick={onClick} disabled={loading}
      className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-border/70 rounded-xl hover:bg-muted transition-colors text-sm font-medium">
      {loading ? <Loader size={16} className="animate-spin" /> : (
        <svg width="18" height="18" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
      )}
      {t('continueGoogle')}
    </button>
  );
}

// ─── Campo input riutilizzabile ────────────────────────────────────────────────
function Field({ icon: Icon, type, placeholder, value, onChange, toggleVisibility, showPass, onFocus, onBlur, autoComplete, dataTestId }) {
  return (
    <div className="relative">
      <Icon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        autoComplete={autoComplete}
        data-testid={dataTestId}
        placeholder={placeholder}
        required
        className="w-full h-10 pl-9 pr-9 text-sm bg-background border border-border/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/20"
      />
      {toggleVisibility && (
        <button type="button" onClick={toggleVisibility}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
          {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      )}
    </div>
  );
}

// ─── Login Modal ─────────────────────────────────────────────────────────────
export function LoginModal({ onClose }) {
  const { t, i18n } = useTranslation();
  const { checkAuth } = useAuth();
  const [tab, setTab] = useState('google');
  const [mode, setMode] = useState('login'); // login | register | forgot | verify
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [error, setError] = useState('');
  const [verifyEmail, setVerifyEmail] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [mascotMode, setMascotMode] = useState('idle'); // idle | watching | hiding
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  // Safe fetch+JSON: legge body come testo poi parsa, evitando "Body is disturbed or locked"
  // (errore tipico se un Service Worker o middleware ha già consumato la response).
  const safeFetch = async (path, options = {}) => {
    const res = await fetch(`${BACKEND_URL}${path}`, {
      headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
      credentials: 'include',
      ...options,
    });
    let data = {};
    try {
      const raw = await res.text();
      if (raw) data = JSON.parse(raw);
    } catch {}
    return { res, data };
  };

  // Google Sign-In diretto via popup → backend verifica il token
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      setError('');
      try {
        const { res, data } = await safeFetch('/api/auth/google-login', {
          method: 'POST',
          body: JSON.stringify({ access_token: tokenResponse.access_token }),
        });
        if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : t('error'));
        if (data.session_token) setAuthToken(data.session_token);
        await checkAuth();
        toast.success(t('welcomeUser', { name: data.name }));
        onClose();
      } catch (e) {
        console.error('[google-login] Error:', e);
        setError(e.message || t('connectionError'));
      } finally {
        setLoading(false);
      }
    },
    onError: (err) => {
      console.error('[google-login] OAuth error:', err);
      setError(t('googleError'));
      setLoading(false);
    },
  });

  const handleGoogle = () => {
    googleLogin();
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { res, data } = await safeFetch('/api/auth/login/email', {
        method: 'POST',
        body: JSON.stringify({ email: form.email, password: form.password }),
      });
      if (!res.ok) {
        const detail = data.detail;
        if (res.status === 403 && detail && typeof detail === 'object' && detail.code === 'email_not_verified') {
          setVerifyEmail(form.email);
          setVerifyCode('');
          setMode('verify');
          toast.info(detail.message || 'Verifica la tua email');
          setLoading(false);
          return;
        }
        throw new Error(typeof detail === 'string' ? detail : (detail?.message || t('error')));
      }
      if (data.session_token) setAuthToken(data.session_token);
      await checkAuth();
      toast.success(t('welcomeBack', { name: data.name }));
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) { setError(t('passwordMismatch')); return; }
    if (form.password.length < 8) { setError(t('passwordTooShort')); return; }
    setLoading(true);
    try {
      const { res, data } = await safeFetch('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name, email: form.email, password: form.password,
          language: (i18n.language || '').split('-')[0],
        }),
      });
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : t('error'));
      if (data.requires_verification) {
        setVerifyEmail(data.email);
        setVerifyCode('');
        setMode('verify');
        toast.success('Codice inviato — controlla la tua email');
        setLoading(false);
        return;
      }
      if (data.session_token) setAuthToken(data.session_token);
      await checkAuth();
      toast.success(t('welcomeNew', { name: data.name }));
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(verifyCode.trim())) { setError('Il codice deve essere di 6 cifre'); return; }
    setLoading(true);
    try {
      const { res, data } = await safeFetch('/api/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify({ email: verifyEmail, code: verifyCode.trim() }),
      });
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Codice non valido');
      if (data.session_token) setAuthToken(data.session_token);
      await checkAuth();
      toast.success(`Benvenuto ${data.name}!`);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || !verifyEmail) return;
    setError('');
    try {
      const { res, data } = await safeFetch('/api/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({
          email: verifyEmail,
          language: (i18n.language || '').split('-')[0],
        }),
      });
      if (!res.ok) {
        if (res.status === 429) {
          setError('Aspetta qualche secondo prima di richiedere un nuovo codice');
          return;
        }
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Errore reinvio');
      }
      toast.success('Nuovo codice inviato');
      setResendCooldown(60);
      const t0 = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) { clearInterval(t0); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await safeFetch('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: form.email }),
      });
      toast.success(t('resetSent', { email: form.email }));
      setMode('login');
    } catch {}
    finally { setLoading(false); }
  };

  const modalContent = (
    <>
      {/* Overlay scuro */}
      <div className="fixed inset-0 bg-black/60 z-[9998]" onClick={onClose} />
      {/* Modal centrato */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div
          className="bg-background border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl overflow-y-auto max-h-[90vh]"
          onClick={e => e.stopPropagation()}
        >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs font-semibold text-amber-500 uppercase tracking-wider mb-0.5">ScanMarket</p>
            <h2 className="text-lg font-bold">
              {mode === 'forgot' ? t('forgotTitle') : mode === 'verify' ? 'Verifica email' : mode === 'register' ? t('registerTitle') : t('loginTitle')}
            </h2>
          </div>
          <button onClick={onClose} data-testid="login-modal-close-btn" className="p-1.5 hover:bg-muted rounded-lg"><X size={16} /></button>
        </div>

        {/* Mascotte panda — visibile su login/register/verify */}
        {mode !== 'forgot' && tab === 'email' && <LoginMascot mode={mascotMode} lookRatio={Math.min(1, (form.email || form.name || '').length / 28)} />}

        {/* Tab Google / Email */}
        {mode !== 'forgot' && mode !== 'verify' && (
          <div className="flex border border-border/70 rounded-xl p-1 mb-4 gap-1">
            <button onClick={() => setTab('google')}
              className={`flex-1 py-1.5 text-sm rounded-lg transition-colors ${tab === 'google' ? 'bg-foreground text-background font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
              Google
            </button>
            <button onClick={() => setTab('email')}
              className={`flex-1 py-1.5 text-sm rounded-lg transition-colors ${tab === 'email' ? 'bg-foreground text-background font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
              Email
            </button>
          </div>
        )}

        {/* Google */}
        {tab === 'google' && mode !== 'forgot' && mode !== 'verify' && (
          <div className="space-y-3">
            <GoogleButton onClick={handleGoogle} loading={loading} t={t} />
            <p className="text-xs text-center text-muted-foreground">
              {t('googleDesc')}
            </p>
          </div>
        )}

        {/* Email Login */}
        {tab === 'email' && mode === 'login' && (
          <form onSubmit={handleEmailLogin} className="space-y-3">
            <Field icon={Mail} type="email" placeholder="tua@email.ch" value={form.email} onChange={set('email')}
              onFocus={() => setMascotMode('watching')} onBlur={() => setMascotMode('idle')}
              autoComplete="email" dataTestId="login-email-input" />
            <Field icon={Lock} type={showPass ? 'text' : 'password'} placeholder={t('passPlaceholder')} value={form.password} onChange={set('password')}
              toggleVisibility={() => setShowPass(v => !v)} showPass={showPass}
              onFocus={() => setMascotMode('hiding')} onBlur={() => setMascotMode('idle')}
              autoComplete="current-password" dataTestId="login-password-input" />
            {error && <p className="text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
              {loading ? <Loader size={14} className="animate-spin mr-2" /> : null}
              {t('loginSubmitBtn')}
            </Button>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <button type="button" onClick={() => { setMode('forgot'); setError(''); }} className="hover:text-foreground">{t('forgotLink')}</button>
              <button type="button" onClick={() => { setMode('register'); setError(''); }} className="hover:text-foreground font-medium" data-testid="login-switch-register-btn">{t('registerLink')}</button>
            </div>
          </form>
        )}

        {/* Email Register */}
        {tab === 'email' && mode === 'register' && (
          <form onSubmit={handleRegister} className="space-y-3">
            <Field icon={User} type="text" placeholder={t('namePlaceholder')} value={form.name} onChange={set('name')}
              onFocus={() => setMascotMode('watching')} onBlur={() => setMascotMode('idle')}
              autoComplete="name" dataTestId="register-name-input" />
            <Field icon={Mail} type="email" placeholder="tua@email.ch" value={form.email} onChange={set('email')}
              onFocus={() => setMascotMode('watching')} onBlur={() => setMascotMode('idle')}
              autoComplete="email" dataTestId="register-email-input" />
            <Field icon={Lock} type={showPass ? 'text' : 'password'} placeholder={t('passPlaceholder')} value={form.password} onChange={set('password')}
              toggleVisibility={() => setShowPass(v => !v)} showPass={showPass}
              onFocus={() => setMascotMode('hiding')} onBlur={() => setMascotMode('idle')}
              autoComplete="new-password" dataTestId="register-password-input" />
            <Field icon={Lock} type={showPass ? 'text' : 'password'} placeholder={t('passConfirmPlaceholder')} value={form.confirm} onChange={set('confirm')}
              onFocus={() => setMascotMode('hiding')} onBlur={() => setMascotMode('idle')}
              autoComplete="new-password" dataTestId="register-confirm-input" />
            {error && <p className="text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2" data-testid="register-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-btn">
              {loading ? <Loader size={14} className="animate-spin mr-2" /> : null}
              {t('createAccountBtn')}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              {t('haveAccount')} <button type="button" onClick={() => { setMode('login'); setError(''); }} className="text-foreground font-medium hover:underline">{t('loginSubmitBtn')}</button>
            </p>
          </form>
        )}

        {/* Verify Email Code */}
        {mode === 'verify' && (
          <form onSubmit={handleVerify} className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Abbiamo inviato un codice a 6 cifre a <strong className="text-foreground break-all">{verifyEmail}</strong>. Inseriscilo qui sotto.
            </p>
            <div className="relative">
              <KeyRound size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                pattern="\d{6}"
                value={verifyCode}
                onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                autoComplete="one-time-code"
                data-testid="verify-code-input"
                className="w-full h-12 pl-9 pr-3 text-center text-lg font-mono tracking-[8px] bg-background border border-border/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/20"
              />
            </div>
            {error && <p className="text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2" data-testid="verify-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading || verifyCode.length !== 6} data-testid="verify-submit-btn">
              {loading ? <Loader size={14} className="animate-spin mr-2" /> : null}
              Verifica e accedi
            </Button>
            <div className="flex items-center justify-between text-xs">
              <button type="button" onClick={() => { setMode('login'); setError(''); }} className="text-muted-foreground hover:text-foreground">
                ← Torna al login
              </button>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendCooldown > 0}
                data-testid="verify-resend-btn"
                className="text-foreground font-medium hover:underline disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
              >
                {resendCooldown > 0 ? `Reinvia (${resendCooldown}s)` : 'Reinvia codice'}
              </button>
            </div>
          </form>
        )}

        {/* Forgot Password */}
        {mode === 'forgot' && (
          <form onSubmit={handleForgot} className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('forgotDesc')}</p>
            <Field icon={Mail} type="email" placeholder="tua@email.ch" value={form.email} onChange={set('email')} />
            {error && <p className="text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? <Loader size={14} className="animate-spin mr-2" /> : null}
              {t('resetSubmitBtn')}
            </Button>
            <button type="button" onClick={() => { setMode('login'); setTab('email'); setError(''); }}
              className="w-full text-xs text-center text-muted-foreground hover:text-foreground">
              {t('backToLogin')}
            </button>
          </form>
        )}
      </div>
      </div>
    </>
  );

  // createPortal bypassa il backdrop-filter dell'Header
  // e rende il modal sempre relativo al viewport, non all'elemento padre
  return createPortal(modalContent, document.body);
}
